import axios from 'axios';

import {
	getSession,
} from 'Vulture/Session.js';

import {
	broncoURL,
	commitHash,
} from 'Vulture/ENV.js';

import API from 'Vulture/lib/API.js';

export const fetchBuildings = async (accountUUID, systemGroupUUID) => {
	const session = getSession();

	let url = `${broncoURL}/buildings`;
	let body = {
		account_uuid: accountUUID,
		system_group_uuid: systemGroupUUID,
		user_uuid: session.user,
	};

	const headers = {
		headers: {
			'X-Auth-Session':      session.session,
			'X-Spark-API-version': commitHash,
		},
	};

	try {
		let res = await axios.post(url, body, headers);
		if (res.status !== 200) {
			debugger;
		}

		return res.data;
	} catch (err) {
		console.error(err);
		return {
			'error':   true,
			'message': err.message,
		};
	}
};

// Per-building write queue. Two controls write the same row — the table's chip and the
// Edit modal — and both POST /v1/buildings/<uuid>. Left unordered they can commit in the
// opposite order to the clicks, leaving the database on the earlier intent while the UI
// shows the later one; ordering the reads cannot fix a write that already landed. Every
// write for one building therefore queues behind the previous one.
const buildingWrites = {};

// Queues one write behind every selected building's in-flight write, and makes it the
// thing the next write for each of those buildings waits on. A bulk delete takes the
// whole selection at once rather than row by row, so two overlapping selections cannot
// each hold part of what the other is waiting for.
const queueBuildingWrites = (buildingUUIDs, write) => {
	const uuids = [ ...new Set(buildingUUIDs), ];

	// A write that failed must not stop the next one. Two things ensure that and either
	// would do: allSettled never rejects, and the tail below swallows the rejection before
	// anything chains onto it. Both are kept — the tail's catch is needed anyway to avoid
	// an unhandled rejection, and allSettled states the intent at the point it matters.
	const pending = uuids.map((uuid) => buildingWrites[uuid]).filter(Boolean);
	const next = Promise.allSettled(pending).then(write);

	const tail = next.catch(() => {}).then(() => {
		// Only the last write for a building clears its entry, so the map does not
		// grow for the life of the page.
		uuids.forEach((uuid) => {
			if (buildingWrites[uuid] === tail) {
				delete buildingWrites[uuid];
			}
		});
	});

	uuids.forEach((uuid) => {
		buildingWrites[uuid] = tail;
	});

	return next;
};

const queueBuildingWrite = (buildingUUID, write) => queueBuildingWrites([ buildingUUID, ], write);

// Writes go through /v1/buildings, which is permission-gated and account-scoped
// server-side. An empty system group is omitted so the column stays NULL rather than
// tripping the system_group FK with "".
const buildingBody = (buildingName, displayName, systemGroupUUID) => {
	const body = {
		building_name: buildingName,
		display_name:  displayName,
	};

	if (systemGroupUUID) {
		body.system_group_uuid = systemGroupUUID;
	}

	return body;
};

// The text to show for a failed write. err.message is makeRequest's
// "HTTP error: <status> - <raw body>", so it is only used when there is no response
// at all (a network failure); otherwise the server's error, or the status.
const writeErrorDetail = (err) => {
	const body = err && err.body;
	const serverDetail = body && typeof body.error === 'string' ? body.error.trim() : '';
	if (serverDetail) {
		return serverDetail;
	}
	if (err && err.status) {
		return `The server rejected the request (HTTP ${err.status}).`;
	}

	return (err && err.message) || 'Unknown error.';
};

export const createBuildings = async (accountUUID, buildingName, selectedSystemGroup, displayName) => {
	// A building must belong to an account; never let the server decide what an
	// empty account means (403 for scoped callers, an unowned row for global ones).
	if (!accountUUID) {
		return {
			'error':   true,
			'message': 'No account selected.',
		};
	}

	const body = {
		account_uuid: accountUUID,
		...buildingBody(buildingName, displayName, selectedSystemGroup),
	};

	try {
		return await API.createRecord('building', body);
	} catch (err) {
		console.error(err);
		return {
			'error':   true,
			'message': writeErrorDetail(err),
		};
	}
};

// Edit: an empty system group unassigns the building; undefined leaves it as it is.
export const updateBuilding = async (buildingUUID, buildingName, selectedSystemGroup, displayName) => {
	const body = {
		building_name: buildingName,
		display_name:  displayName,
	};
	if (selectedSystemGroup !== undefined) {
		body.system_group_uuid = selectedSystemGroup || null;
	}

	try {
		return await queueBuildingWrite(buildingUUID, () => API.updateRecord('building', buildingUUID, body));
	} catch (err) {
		console.error(err);
		return {
			'error':   true,
			'message': writeErrorDetail(err),
		};
	}
};

// Assign / unassign only. Used by the table's row chip, which persists immediately
// rather than staging into the page's pending changes. null unassigns.
export const setBuildingSystemGroup = async (buildingUUID, systemGroupUUID) => {
	try {
		return await queueBuildingWrite(buildingUUID, () => API.updateRecord('building', buildingUUID, {
			system_group_uuid: systemGroupUUID || null,
		}));
	} catch (err) {
		console.error(err);
		return {
			'error':   true,
			'message': writeErrorDetail(err),
		};
	}
};

// Matches batchMaxActions in bronco's /v1/batch handler.
const MAX_BATCH_DELETES = 200;

// A failed delete only proves nothing was deleted when the server answered with a
// refusal: a batch failure names its failed_index (the transaction rolled back), and a
// 4xx is refused before any write. A network error, a gateway 502/504 or a 500 without
// failed_index (a failed commit) can arrive after the rows are already gone.
const deleteOutcomeKnown = (err) => {
	const body = err && err.body;
	if (body && typeof body.failed_index === 'number') {
		return true;
	}

	return Boolean(err && err.status >= 400 && err.status < 500);
};

// Names the building the delete stopped on. A batch reports which action failed;
// a single delete can only be about the one building we sent.
const failedBuildingMessage = (err, buildingList) => {
	const body = err && err.body;

	let index = -1;
	if (body && typeof body.failed_index === 'number') {
		index = body.failed_index;
	} else if (buildingList.length === 1) {
		index = 0;
	}

	const building = index >= 0 ? buildingList[index] : null;
	const name = building && building.vals ? building.vals.buildingName : '';
	const detail = writeErrorDetail(err);
	// Only a multi-select has other buildings whose fate needs explaining, and only a
	// known refusal says what that fate was.
	let suffix = '';
	if (!deleteOutcomeKnown(err)) {
		suffix = buildingList.length > 1 ?
			' Some or all of the buildings may have been deleted; the list has been refreshed.' :
			' The building may have been deleted; the list has been refreshed.';
	} else if (buildingList.length > 1) {
		suffix = ' No buildings were deleted.';
	}

	if (name) {
		return `Could not delete "${name}": ${detail}${suffix}`;
	}

	return `${detail}${suffix}`;
};

// Deleting ONE building uses the plain single-record route: it needs no
// transaction, and that route is the long-standing one. Only the multi-select
// path uses /v1/batch, where atomicity across rows is the whole point —
// previously it fired one DELETE per building and swallowed every rejection, so
// a partly-refused selection left some rows behind with no error shown.
//
// Both shapes resolve the same way: the batch response, or { error, message }.
export const deleteBuildings = async (buildingList, accountUUID) => {
	if (!buildingList || buildingList.length === 0) {
		return {};
	}

	// Mirrors the batch endpoint's cap. Chunking would silently give up the
	// all-or-nothing guarantee, so say so instead.
	if (buildingList.length > MAX_BATCH_DELETES) {
		return {
			'error':   true,
			'message': `Select ${MAX_BATCH_DELETES} or fewer buildings at a time (${buildingList.length} selected).`,
		};
	}

	try {
		// Inside the try: a row without m1Key would otherwise throw past both
		// call sites, and neither has a .catch().
		const uuids = buildingList.map((building) => building.m1Key.split('.')[1]);

		// Both shapes queue, for the same reason: a delete that overtakes an in-flight
		// chip update leaves that update running against a soft-deleted row, so it 404s
		// after the delete has already succeeded and the user is shown a failure for a
		// write that was never going to matter. The batch holds row locks for the length
		// of its transaction, so an update that starts alongside it waits on InnoDB
		// instead of failing quickly.
		if (uuids.length === 1) {
			return await queueBuildingWrites(uuids, () => API.deleteRecord('building', uuids[0]));
		}

		return await queueBuildingWrites(uuids, () => API.batch(uuids.map((uuid) => ({
			op: 'delete',
			m1: 'building',
			uuid,
		})), accountUUID));
	} catch (err) {
		console.error(err);
		return {
			'error':          true,
			'message':        failedBuildingMessage(err, buildingList),
			'outcomeUnknown': !deleteOutcomeKnown(err),
		};
	}
};